import { hasTrackingConsent } from '../consent/consent';
import { getPageKey } from '../utils/getPageKey';

const retryableStatusCodes = [
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
];

type Environment = 'prod' | 'non-prod';

// biome-ignore lint/suspicious/noExplicitAny: Set to any for backwards compatibility, should be fixed in a future major version
type EventDataType = any;
export type EventData = { [key: string]: EventDataType };
type ProcessedEvent = { pageKey: string; data: EventData };
type PatchEventOptions = {
  strategy?: 'current_page' | 'all_pages';
  eventNames?: string[];
  overwrite?: boolean;
};

type PageQueues = {
  [key: string]: EventData[];
};

export class EventBatchTransporter {
  /**
   * @deprecated queue is deprecated in favor of pageQueue.
   */
  private queue: EventData[] = [];

  /**
   * Queue for events that are not yet sent.
   * This is used to group events by page (by url).
   * The key is the page url and the value is an array of events.
   */
  private pageQueues: PageQueues = {};
  private maxBatchSize: number;
  private flushInterval: number;
  private maxPostRetries: number;
  private maxSendAttempts: number;
  private retryDelay: number;
  private flushTimer: NodeJS.Timeout | null = null;
  private endpoint: string | undefined;
  private failedSendAttempts: number;
  private logging = false;

  constructor({ environment }: { environment?: Environment } = {}) {
    this.maxBatchSize = 10;
    this.flushInterval = 10000;
    this.maxPostRetries = 3;
    this.maxSendAttempts = 3;
    this.retryDelay = 1000;
    this.endpoint = this.getEndpoint(environment);
    this.failedSendAttempts = 0;

    if (typeof window !== 'undefined') {
      // Send batches when browser url is changed, user switches tab or browser is minimized
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.sendBatches({ useBeacon: true });
        }
      });
    }
  }

  private startFlushTimer() {
    if (!this.flushTimer) {
      this.log(`Starting flush timer with interval: ${this.flushInterval}ms`);
      this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
    }
  }

  private stopFlushTimer() {
    if (this.flushTimer) {
      this.log('Stopping flush timer');
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private log(message: string, { force } = { force: false }) {
    if (this.logging || force) {
      console.log(`[EventBatchTransporter]: ${message}`);
    }
  }

  public toggleLogging() {
    this.logging = !this.logging;
    this.log(`Logging ${this.logging ? 'enabled' : 'disabled'}`, {
      force: true,
    });
  }

  public patchQueuedEvents(properties: EventData, opts?: PatchEventOptions) {
    const { strategy = 'all_pages', eventNames, overwrite = true } = opts ?? {};
    this.log(
      `Patching queued events with strategy: ${strategy} and event names: ${eventNames}`,
    );
    const events: EventData[] = [];
    if (strategy === 'current_page') {
      const pageKey = getPageKey();
      const pageQueue = this.pageQueues[pageKey];
      if (!pageQueue) {
        this.log(`No page queue found for key: ${pageKey}`);
        return;
      }
      events.push(...pageQueue);
    } else {
      events.push(
        ...Object.values(this.pageQueues).reduce(
          (acc, pageItems) => acc.concat(pageItems),
          [] as EventData[],
        ),
      );
    }
    if (!events.length) {
      this.log('Found no events to patch');
      return;
    }
    this.log(`Patching ${events.length} queued events`);
    for (const event of events) {
      if (eventNames && !eventNames.includes(event.event)) {
        continue;
      }
      if (overwrite) {
        Object.assign(event, properties);
      } else {
        for (const key of Object.keys(properties)) {
          if (event[key] === undefined && properties[key] !== undefined) {
            event[key] = properties[key];
          }
        }
      }
    }
  }

  queueEvent(eventData: EventData) {
    if (!this.endpoint) {
      return;
    }

    // This is for backwards compatibility, should be removed in a future major version
    this.queue.push(eventData);

    const pageKey = getPageKey();
    const pageQueue = this.getPageQueueByKey(pageKey);
    pageQueue.push(eventData);
    this.log(
      `Queued event: \n Key: ${pageKey} \n Event: ${JSON.stringify(eventData)}`,
    );
    if (this.getPageQueueEventsTotal() >= this.maxBatchSize) {
      this.log(
        `PageQueue has more than ${this.maxBatchSize} events, flushing immediately`,
      );
      this.flush();
    } else {
      this.startFlushTimer();
    }
  }

  private getPageQueueByKey(key: string) {
    let pageQueue: Required<PageQueues[number]>;
    if (this.pageQueues[key]) {
      pageQueue = this.pageQueues[key];
    } else {
      pageQueue = [];
      this.pageQueues[key] = pageQueue;
    }
    return pageQueue;
  }

  private getPageQueueEventsTotal() {
    return Object.values(this.pageQueues).reduce(
      (acc, pageItems) => acc + (pageItems?.length ?? 0),
      0,
    );
  }

  private getEndpoint(environment?: Environment) {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);

    if (url.hostname === 'localhost') {
      return;
    }

    const siteSlug = url.pathname.split('/')[1]?.toLowerCase();

    const isChina = siteSlug === 'zh-cn';

    if (isChina) {
      return;
    }

    if (environment === 'prod') {
      return 'https://www.volvocars.com/api/t/v1';
    }

    if (environment === 'non-prod') {
      return 'https://qawww.volvocars.com/api/t/v1';
    }

    return `${url.origin}/api/t/eu/v1`;
  }

  // biome-ignore lint/suspicious/useAwait: Intended
  private async sendBeacon(payload: string) {
    if (!this.endpoint) {
      return;
    }

    const success = window.navigator.sendBeacon(
      this.endpoint,
      new Blob([payload], {
        type: 'application/json',
      }),
    );

    if (!success) {
      throw new Error('Failed to send batch with Beacon');
    }
  }

  private async fetchWithRetry(payload: string, attempt = 1): Promise<void> {
    if (!this.endpoint) {
      return;
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        body: payload,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        // Sends the request even after navigating away from the page
        keepalive: true,
      });

      if (!response.ok) {
        const error = new Error(`Failed sending batch: ${response.status}`);
        this.log(`Failed sending batch: ${response.status}`);
        if (!retryableStatusCodes.includes(response.status)) {
          error.name = 'NoRetryError';
        }

        throw error;
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'NoRetryError') {
        throw error;
      }

      if (attempt < this.maxPostRetries) {
        //Exponential backoff
        this.log(
          `Retrying sending batch in ${this.retryDelay * 2 ** (attempt - 1)}ms, attempt ${attempt} of ${this.maxPostRetries}`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, this.retryDelay * 2 ** (attempt - 1)),
        );

        return this.fetchWithRetry(payload, attempt + 1);
      }

      throw error;
    }
  }

  /**
   * Generates events from the page queue
   */
  private processPageQueueEvents() {
    const events: ProcessedEvent[] = [];
    for (const [pageKey, page] of Object.entries(this.pageQueues)) {
      const processedPageEvents = page.map((data) => ({ pageKey, data }));
      events.push(...processedPageEvents);
      this.pageQueues[pageKey] = [];
    }
    return events;
  }
  private returnProcessedEventsToQueue(events: ProcessedEvent[]) {
    this.log('Failed sending chunk, returning events to queue');
    for (const event of events) {
      this.pageQueues[event.pageKey].push(event.data);
    }
  }

  private async sendBatches({ useBeacon = false } = {}) {
    if (this.getPageQueueEventsTotal() === 0) {
      return;
    }
    if (!hasTrackingConsent()) {
      this.log('No tracking consent, canceling sending batches');
      return;
    }

    // Stop sending anything if we have failed 3 sendBatches calls continuosly
    // without any success, meaning:
    // 3(failedSendAttempts) * 3(retries) + 3(initial calls) = 12 times in total
    if (this.failedSendAttempts > this.maxSendAttempts) {
      this.log(
        'Failed sending batches too many times, stopping sending batches',
      );
      this.stopFlushTimer();
      return;
    }

    const shouldUseBeacon = useBeacon || document.visibilityState === 'hidden';

    const processedEvents: ProcessedEvent[] = this.processPageQueueEvents();

    const chunks: ProcessedEvent[][] = [];

    while (processedEvents.length > 0) {
      chunks.push(processedEvents.splice(0, this.maxBatchSize));
    }

    const results = await Promise.allSettled(
      chunks.map((chunk) => {
        const data = JSON.stringify({ events: chunk.map((e) => e.data) });
        return shouldUseBeacon
          ? this.sendBeacon(data)
          : this.fetchWithRetry(data);
      }),
    );

    results.forEach((res, index) => {
      if (res.status === 'rejected') {
        this.failedSendAttempts++;
        this.returnProcessedEventsToQueue(chunks[index]);
      }
    });

    if (this.getPageQueueEventsTotal() === 0) {
      this.failedSendAttempts = 0;
      this.stopFlushTimer();
    }
  }

  private flush() {
    this.log('Flushing events');
    this.sendBatches();
  }

  /**
   * Clears the queues and stops the flush timer.
   */
  reset() {
    this.log('Resetting, clearing queues');
    this.queue = [];
    this.pageQueues = {};
    this.failedSendAttempts = 0;
    this.stopFlushTimer();
  }
}

// Singleton so we batch properly across apps
// even with multiple instances of Tracker
export function getEventBatchTransporter() {
  if (typeof window === 'undefined') {
    return;
  }

  if (!window.VolvoCarsAnalytics?.eventBatchTransporter) {
    const eventBatchTransporter = new EventBatchTransporter();
    attachEventBatchTransporter(eventBatchTransporter);

    return eventBatchTransporter;
  }

  return window.VolvoCarsAnalytics.eventBatchTransporter;
}

function attachEventBatchTransporter(
  eventBatchTransporter: EventBatchTransporter,
) {
  if (!('VolvoCarsAnalytics' in window)) {
    Object.defineProperty(window, 'VolvoCarsAnalytics', {
      value: {},
      enumerable: true,
      writable: false,
      configurable: false,
    });
  }

  Object.defineProperty(window.VolvoCarsAnalytics, 'eventBatchTransporter', {
    value: eventBatchTransporter,
    enumerable: true,
    writable: false,
    configurable: true,
  });
}
